import React from 'react';
import { TaxRegionKey } from '@capintel/instruments-lib';
import { TaxCurrencyInput, TaxPercentageInput } from './components';
import { FIELD_KEYS, FIELD_LABEL_KEYS } from '../../hooks/useTaxAnalysisInputs/constants';
import { useTaxAnalysisInputsCA } from '../../hooks/useTaxAnalysisInputs/useTaxAnalysisInputsCA';
import { formatMessage } from '@/utils/i18n';
import { PartialCaComparisonTaxSettings } from '@/types/tax-analysis';

const CA_FIELD_KEYS = FIELD_KEYS[TaxRegionKey.CA];
const CA_FIELD_LABEL_KEYS = FIELD_LABEL_KEYS[TaxRegionKey.CA];

interface TaxAnalysisInputsContainerCAProps {
  readOnly?: boolean;
}

const INPUT_TYPE = {
  CURRENCY: 'currency',
  PERCENTAGE: 'percentage',
} as const;

type InputType = (typeof INPUT_TYPE)[keyof typeof INPUT_TYPE];

interface InputConfig {
  key: keyof PartialCaComparisonTaxSettings;
  type: InputType;
}

const INPUT_CONFIGS: InputConfig[] = [
  { key: CA_FIELD_KEYS.YTD_GAIN, type: INPUT_TYPE.CURRENCY },
  { key: CA_FIELD_KEYS.YTD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: CA_FIELD_KEYS.CARRY_FORWARD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: CA_FIELD_KEYS.MARGINAL_TAX_RATE, type: INPUT_TYPE.PERCENTAGE },
];

// This component is for Canadian tax inputs specifically
const TaxAnalysisInputsContainerCA: React.FC<TaxAnalysisInputsContainerCAProps> = ({ readOnly = false }) => {
  const taxInputs = useTaxAnalysisInputsCA();

  const { values, setYTDGain, setYTDLoss, setCarryForwardLoss, setMarginalTaxRate } = taxInputs;

  const setterMap: Record<keyof PartialCaComparisonTaxSettings, (value?: number) => void> = {
    [CA_FIELD_KEYS.YTD_GAIN]: setYTDGain,
    [CA_FIELD_KEYS.YTD_LOSS]: setYTDLoss,
    [CA_FIELD_KEYS.CARRY_FORWARD_LOSS]: setCarryForwardLoss,
    [CA_FIELD_KEYS.MARGINAL_TAX_RATE]: setMarginalTaxRate,
  };

  return (
    <div className="tax-inputs-container">
      {INPUT_CONFIGS.map(({ key, type }) => {
        const InputComponent = type === INPUT_TYPE.CURRENCY ? TaxCurrencyInput : TaxPercentageInput;
        return (
          <InputComponent
            key={key}
            id={key}
            label={formatMessage(CA_FIELD_LABEL_KEYS[key])}
            value={values[key]}
            onChange={setterMap[key]}
            disabled={readOnly}
          />
        );
      })}
    </div>
  );
};

export default TaxAnalysisInputsContainerCA;
